import { Table, Button, Tooltip } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loggingStore } from '../store/logging'
import { useTranslation } from 'react-i18next'
import { RNCommon, RNLogging } from '../helpers/routeNames'
import { ArrowRightOutlined } from '@ant-design/icons'
import { getLoggingSource, getLoggingType } from '../helpers/logging'

interface Props {
	id: number
	maxRows?: number | null
	scrollY?: number | null
}

const CarrierOneLogging: FC<Props> = ({
	id,
	maxRows = 10,
	scrollY = 300
}) => {
	const { loggings } = loggingStore()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined'
			? window.innerWidth < 768
			: false
	)

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768)
		}
		window.addEventListener('resize', handleResize)
		return () =>
			window.removeEventListener('resize', handleResize)
	}, [])

	const translateLogValue = (value: string | undefined) =>
		value ? t(value, { defaultValue: value }) : '-'

	const logs = loggings
		.filter((l) => l.carrierId === id)
		.sort((a, b) => {
			const dateA = new Date(a.happenedAt).getTime()
			const dateB = new Date(b.happenedAt).getTime()
			return dateB - dateA
		})

	const visibleLogs =
		maxRows == null ? logs : logs.slice(0, maxRows)

	return (
		<Table
			title={() => (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center'
					}}>
					<h2 style={{ margin: 0 }}>
						{t('EventLog')}
						{maxRows != null ? ` • ${t('Recent')}` : ''}
					</h2>
					<Tooltip title={t('ViewAll')}>
						<Button
							type="primary"
							icon={<ArrowRightOutlined />}
							hidden={
								location.pathname.includes(
									RNCommon
								) || isMobile
							}
							onClick={() =>
								navigate(`${RNLogging}/${id}`)
							}
							size="small">
							{t('ViewAll')}
						</Button>
					</Tooltip>
				</div>
			)}
			dataSource={visibleLogs}
			locale={{ emptyText: t('NoData') }}
			scroll={scrollY == null ? undefined : { y: scrollY }}
			pagination={{
				position: ['bottomRight'],
				pageSize: 10,
				hideOnSinglePage: true,
				showSizeChanger: false
			}}
			columns={[
				{
					title: '№',
					key: 'id',
					width: 70,
					render: (_: unknown, row: any) =>
						row.lid ?? row.id
				},
				{
					title: t('Time'),
					dataIndex: 'happenedAt',
					key: 'happenedAt',
					render: (happenedAt: string) => {
						const date = new Date(happenedAt)
						return date.toLocaleString('ru')
					},
					width: 170
				},
				{
					title: t('Type'),
					key: 'type',
					render: (_: unknown, row: any) =>
						getLoggingType(row),
					width: 80
				},
				{
					title: t('Source'),
					key: 'source',
					render: (_: unknown, row: any) =>
						translateLogValue(getLoggingSource(row)),
					width: 145
				},
				{
					title: t('EventText'),
					dataIndex: 'text',
					key: 'text',
					width: 300,
					render: (text: string) => (
						<span
							style={{
								lineHeight: 1
							}}>
							{text}
						</span>
					)
				}
			]}
		/>
	)
}

export default CarrierOneLogging
